import { PaymentElement, useCheckout } from '@stripe/react-stripe-js/checkout';
import { StripeCheckoutConfirmResult } from '@stripe/stripe-js';
import { css } from 'aphrodite';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useErrorMessages } from '../../../../../../../../models/hooks/appStateHooks';
import { Button } from '../../../../../../../../web/components/Button';
import { LoadingLogo } from '../../../../../../../../web/components/LoadingLogo';
import { TextInput } from '../../../../../../../../web/components/TextInput';
import { XIcon } from '../../../../../../../../web/components/svgs/icons/XIcon';
import { bs } from '../../../../../../../../web/styles/styles';
import { styleSheet } from '../styles';

interface StripeCheckoutFormProps {
	onSuccessClose: (sessionId: string) => void;
	onCloseModal: (sessionId: string) => void;
	attendeeEmail: string;
}

export const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
	onSuccessClose,
	onCloseModal,
	attendeeEmail,
}) => {
	const checkout = useCheckout();

	const [isLoading, setIsLoading] = React.useState(false);
	const [isScriptLoading, setIsScriptLoading] = React.useState(true);
	const [email, setEmail] = React.useState<string>(attendeeEmail || '');
	const errors = useErrorMessages();

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const location = useLocation();

	React.useEffect(() => {
		// If the user navigates away from the page, close the modal
		if (location.search && checkout?.type === 'success') {
			onSuccessClose(checkout.checkout.id);
		}
	}, [location.search, onSuccessClose, checkout]);

	React.useEffect(() => {
		if (checkout?.type !== 'success') {
			return;
		}

		if (checkout?.checkout.id) {
			setTimeout(() => {
				setIsScriptLoading(false);
			}, 1000);
		}
	}, [checkout]);

	/**
	 * Handle Stripe payment form submission
	 *
	 * Flow:
	 * 1. Validate email and prevent duplicate submissions
	 * 2. Call Stripe checkout.confirm() with user email
	 * 3. On success: Stripe webhook fires on backend
	 * 4. Backend webhook: Processes pooled response and creates/updates survey response
	 * 5. Backend webhook: Generates new token for the created/updated response
	 * 6. Frontend: Retrieves new token via checkout session endpoint
	 * 7. Frontend: Redirects to survey page with new token
	 *
	 * @param event - Form submission event
	 */
	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.
		event.preventDefault();
		event.stopPropagation(); // Prevent the event from bubbling up
		// Prevent the form from being submitted again while processing
		// the payment
		setIsLoading(true);
		setErrorMessage(null);

		if (!checkout || checkout.type !== 'success') {
			setErrorMessage('Payment processing failed. Please try again.');
			setIsLoading(false);
			return;
		}
		if (!email?.includes('@')) {
			setErrorMessage('Please enter a valid email address.');
			setIsLoading(false);
		}

		try {
			const result: StripeCheckoutConfirmResult = await checkout.checkout.confirm({
				// Use the email entered by the user
				email,
			});

			if (result.type === 'error') {
				setErrorMessage(result.error.message);
				setIsLoading(false);

				return;
			}
			if (result.type === 'success') {
				setIsLoading(false);
				onSuccessClose(checkout.checkout.id);
			}
		} catch (error) {
			errors.push(error);
			setErrorMessage('Oops! Something went wrong. Please try again.');
			setIsLoading(false);
		}
	};

	return (
		<>
			<header className={css(styleSheet.header)}>
				<Button
					type='button'
					kind='custom'
					size='normal'
					className={css(styleSheet.closeButton)}
					disabled={isLoading}
					onClick={() => {
						if (checkout.type === 'success') {
							onCloseModal(checkout.checkout.id);
						}
					}}
				>
					<XIcon />
					close
				</Button>
			</header>
			{
				<form onSubmit={handleSubmit}>
					<section className={css(bs.mb2, bs.pr1)}>
						<h3 className={css(styleSheet.paymentInfoTitle)}>Your Order</h3>
						{checkout.type === 'success' &&
							checkout.checkout.lineItems.map(item => (
								<div key={item.id} className={css(styleSheet.orderInfo)}>
									<p>
										{item.name} <span className={css(styleSheet.timesSymbol)}>&#215;</span>{' '}
										<strong>{item.quantity}</strong>
									</p>
								</div>
							))}
					</section>
					<div>
						<label htmlFor='email' className={css(styleSheet.label)}>
							Email
						</label>
						<TextInput
							id='email'
							inputId='email'
							type='email'
							className={css(bs.fullWidth, bs.mb2)}
							onChange={e => setEmail(e.target.value)}
							value={email}
							placeholder='Email'
						/>
					</div>
					{isScriptLoading ? (
						<div className={css(bs.flex, bs.flexCenter)}>
							<LoadingLogo size={50} />
						</div>
					) : null}
					<PaymentElement id='payment-element' />
					<Button
						type='submit'
						disabled={isLoading}
						kind='primary'
						size='normal'
						isLoading={isLoading}
						className={css(styleSheet.submitButton)}
						loadingSpinnerSize='extra-small'
					>
						Pay {checkout.type === 'success' && checkout.checkout.total.total.amount}
					</Button>
					{errorMessage && <small className={css(styleSheet.errorMessage)}>{errorMessage}</small>}
				</form>
			}
		</>
	);
};
